import { TransactionsPairForReconciliation } from "@common/contracts/reconciliation.contract";
import { useContext, useMemo } from "react";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import classNames from "classnames";
import { DragHandle6DotIcon } from "@icons";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    determineFinancialTransactionDateAfterReconciliation,
    getStatmentTransactionId,
} from "@common/helpers/reconciliation";
import { ReconciliationTransaction } from "./ReconciliationTransaction";
import { ReconciliationContext } from "./Reconciliation.context";
import { ReconciliationDateUpdateReason } from "./helpers";
import styles from "./Reconciliation.module.scss";

interface Props {
    pair: TransactionsPairForReconciliation;
    isDragging: boolean;
}

export const ReconciliationAccountTransaction: React.FC<Props> = ({
    pair,
    isDragging,
}) => {
    const {
        financialTransactionsToRemoveIdsSet,
        statementTransactionsToAddIdsSet,
        financialTransactionIdsIgnoringDateChanges,
    } = useContext(ReconciliationContext);

    const isRemoved =
        pair.fromAccount &&
        financialTransactionsToRemoveIdsSet.has(
            pair.fromAccount.financialTransactionId,
        );
    const isAdded =
        pair.fromStatement &&
        statementTransactionsToAddIdsSet.has(
            getStatmentTransactionId(pair.fromStatement),
        );

    const dateToUpdate = useMemo(() => {
        const toUpdate =
            pair.fromAccount &&
            determineFinancialTransactionDateAfterReconciliation(
                pair.fromAccount,
                pair.fromStatement,
                financialTransactionIdsIgnoringDateChanges,
            );

        if (toUpdate && toUpdate !== pair.fromAccount?.date) {
            return toUpdate;
        }
    }, [
        pair.fromAccount,
        pair.fromStatement,
        financialTransactionIdsIgnoringDateChanges,
    ]);

    const dateUpdateReason = useMemo(() => {
        if (pair.fromStatement) {
            return ReconciliationDateUpdateReason.MATCHED_WITH_STATMENT;
        }
    }, [pair]);

    const shouldShowDragIcon = !isDragging;
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: pair.fromAccount ? pair.fromAccount.financialTransactionId : "",
        disabled: !pair.fromAccount,
        data: {
            amount: pair.fromAccount?.amount,
            date: pair.fromAccount?.date,
            isCashItem: pair.fromAccount?.isCashItem,
        },
    });

    const style = {
        transform: CSS.Translate.toString(transform),
    };

    return (
        <div
            ref={setNodeRef}
            className={classNames(styles.accountTransaction, {
                [styles.draggableAccountTransaction]: pair.fromAccount,
            })}
            style={style}
        >
            <ReconciliationTransaction
                transaction={isAdded ? pair.fromStatement : pair.fromAccount}
                isAdded={isAdded}
                isRemoved={isRemoved}
                dateToUpdate={dateToUpdate}
                dateUpdateReason={dateUpdateReason}
                dragIcon={
                    pair.fromAccount &&
                    shouldShowDragIcon && (
                        <Tooltip content="Drag'n'drop Kick transaction to match it to a different transaction from statement">
                            <span
                                className={styles.dragIcon}
                                {...attributes}
                                {...listeners}
                            >
                                <DragHandle6DotIcon className="icon-color-gray-900" />
                            </span>
                        </Tooltip>
                    )
                }
            />
        </div>
    );
};
